import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function formatApiError(e) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Error";
}

export default function Perfil() {
  const { user } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const backHref = user?.role === "admin" ? "/admin" : user?.role === "cliente" ? "/cliente" : "/repartidor";

  const submit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    if (newPw.length < 8 || !/[A-Za-z]/.test(newPw) || !/\d/.test(newPw)) {
      toast.error("Mínimo 8 caracteres, con letras y números");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Contraseña actualizada. Volvé a iniciar sesión si querés.");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white bg-noise pb-16">
      <header className="border-b border-white/10 bg-zinc-950/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to={backHref} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="perfil-back">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">/ Perfil</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold mb-2" data-testid="perfil-title">Tu cuenta</h1>
        <div className="text-zinc-500 font-mono-tech text-sm mb-8">{user?.email} · <span className="text-yellow-300 uppercase">{user?.role}</span></div>

        <section className="border border-white/10 bg-zinc-900/60 p-6">
          <div className="flex items-center gap-2 font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-6">
            <KeyRound className="w-4 h-4" /> Cambiar contraseña
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mb-2">Contraseña actual</label>
              <input
                type="password"
                required
                value={oldPw}
                onChange={(e) => setOldPw(e.target.value)}
                data-testid="old-password"
                className="w-full bg-zinc-950 border border-white/10 focus:border-yellow-300 outline-none p-3 font-mono-tech text-sm"
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mb-2">Nueva contraseña (mín. 8, con letras + números)</label>
              <input
                type="password"
                required
                minLength={8}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="new-password"
                className="w-full bg-zinc-950 border border-white/10 focus:border-yellow-300 outline-none p-3 font-mono-tech text-sm"
              />
            </div>
            <div>
              <label className="block font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mb-2">Confirmar nueva contraseña</label>
              <input
                type="password"
                required
                minLength={8}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                data-testid="confirm-password"
                className="w-full bg-zinc-950 border border-white/10 focus:border-yellow-300 outline-none p-3 font-mono-tech text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-password"
              className="w-full sm:w-auto bg-yellow-300 text-black font-bold px-6 py-3 uppercase tracking-widest inline-flex items-center gap-2 hover:brightness-95 disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar"}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
}
